/* Sopra i fogli, sotto l'accesso.
   Un pannello a tutto schermo e' per costruzione l'ultima cosa che si e' aperta: ci
   si arriva premendo qualcosa che sta DENTRO cio' che copre — il profilo di un
   giocatore dalla lista dei collegati, o dalla Sfida diretta. Sotto i fogli si
   premeva un nome e il profilo si apriva DIETRO: invisibile e inservibile.
   Finche' quei fogli vivevano dentro `.dash` — un contesto d'impilamento a
   `z-index: 1` — il difetto non si vedeva, ma bastava un foglio montato fuori
   di li' (quello globale di App) per scoprirlo.
   Il tetto resta l'accesso: quello sale sopra tutto di proposito, e un pannello
   che lo coprisse lascerebbe senza porta chi deve entrare. */
.fs-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-foglio-partita);
  background: var(--overlay-veil);
  /* Niente sfocatura, e di proposito: `.fs-panel` qui dentro e' opaco e alto
     `100svh`, quindi di questo fondo non si vede un pixel. Sfocare sarebbe una
     passata di GPU su cio' che nessuno guarda. Il colore resta il token perche'
     nell'istante dell'animazione, prima che il pannello sia steso, quel che si
     intravede e' il velo dell'app e non un grigio suo. */
  display: flex;
  flex-direction: column;
}

.fs-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100svh;
  background: var(--color-bg, #fff);
  /* Respect the notch / home indicator on phones. */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
}

/* Chrome matched to InGameSettingsModal: neutral header with a bottom border,
   serif title and a circular close button — so Moves/Chat and Settings share
   the same full-screen overlay look. */
.fs-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.08));
  /* Serve solo quando la testata ha un'azione (`azioneTitolo`): con due figli lo
     spazio fra loro lo faceva gia' `space-between`, e questo gap non si vedeva. */
  gap: var(--spacing-sm);
}

/* L'ELLISSI non e' estetica, e i numeri sono MISURATI a 360px in Chromium, non
   stimati. Con la bandierina accanto al titolo questo ne ha 220,4px; un nome da
   30 caratteri — il massimo che la registrazione accetta — a 1,15rem Fraunces ne
   chiede 307. Senza queste righe tornerebbe a capo e la testata diventerebbe due
   righe.

   Le tre dichiarazioni vanno INSIEME: `text-overflow` senza `overflow: hidden` e
   senza `nowrap` non produce nessun puntino, e `min-width: 0` serve perche' in un
   contenitore flex il valore iniziale e' `auto`, cioe' «non restringermi sotto il
   mio contenuto» — ed e' quello che spingerebbe i bottoni fuori. */
.fs-panel-title {
  margin: 0;
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-text, #1B1F20);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* L'azione accanto al titolo: stessa misura e stessa forma della chiusura.
   La testata non cresce di un pixel, ed e' misurato: 77px con l'azione e 77px
   senza, perche' il suo elemento piu' alto era gia' un bottone da 44. E' la
   ragione per cui qui non serve nessuno dei trucchi che servivano dentro la card
   del profilo, dove la riga del nome e' alta 22,4px e il bersaglio da 44 andava
   tolto dal flusso con un margine negativo.
   Il colore lo mette chi la usa (il pannello del profilo la vuole grigia, e rossa
   allo sfioramento). */
.fs-panel-azione {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--touch-target-min, 44px);
  height: var(--touch-target-min, 44px);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.15s, color 0.15s;
}

.fs-panel-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* La chiusura resta ATTACCATA al bordo destro anche quando la testata ha tre
     figli, e il titolo con la sua azione restano insieme a sinistra.
     Il margine automatico e `space-between` non sono due modi di dire la stessa
     cosa, e nessuno dei due e' di troppo: i margini `auto` si prendono lo spazio
     libero PRIMA che `justify-content` lo distribuisca, quindi con tre figli
     vince questo e con due il risultato e' identico a com'era. Togliendo l'uno o
     l'altro, uno dei due casi si rompe. */
  margin-inline-start: auto;
  /* Mobile-First Mandate: interactive elements never go below 44×44. */
  width: var(--touch-target-min, 44px);
  height: var(--touch-target-min, 44px);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.15s;
}

.fs-panel-close:hover {
  background: var(--color-veil);
  color: var(--color-text);
}

.fs-panel-close:active {
  background: var(--color-veil-strong);
}

.fs-panel-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Make the hosted Moves / Chat panels fill the full screen and own their
   scroll. They were sized for a narrow sidebar; here they go edge to edge. */
.fs-panel-body .chat-panel,
.fs-panel-body .chat-panel.open,
.fs-panel-body .move-list {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: none;
  max-height: none;
  border: none;
  border-radius: 0;
}

/* The overlay already provides a "Chat" header with a close button, so the
   panel's own collapsible header (whose toggle would hide the messages) is
   redundant here. */
.fs-panel-body .chat-header {
  display: none;
}
